<template>
  <div class="payment-mode pb-10">
    <div class="title-container">
      <p class="form-title">Use Break Konnect payment system</p>
      <p class="form-subtitle">
        Connect your bank account to one of the payment clients below to recieve payment for event directly to your
        account!
      </p>
      <p class="form-subtitle">Your payout will be issued within 2 business days after the event is over.</p>
    </div>

    <VSkeletonLoader
      v-if="isLoading"
      type="card"
    />

    <div
      v-if="accounts.length > 0"
      class="connected-accounts"
    >
      <div
        v-for="account in accounts"
        :key="account.id"
        class="connected-accounts-item"
      >
        <button
          :class="[
            'connected-account',
            { 'connected-account--selected': selectedAccountId === account.id },
            { 'connected-account--pending': !account.onboardingComplete },
          ]"
          @click="account.onboardingComplete && handleSelectAccount(account.id)"
        >
          <VMenu
            bottom
            left
            :close-on-content-click="true"
          >
            <template #activator="{ on, attrs }">
              <span
                class="dots-menu"
                v-bind="attrs"
                v-on="on"
                @click.stop
              >
                <VIcon size="20">{{ mdiDotsHorizontal }}</VIcon>
              </span>
            </template>
            <VList class="account-context-menu">
              <VListItem
                v-if="!account.onboardingComplete"
                @click="handleContinueOnboarding(account.id)"
              >
                <VListItemTitle>
                  <VIcon
                    size="18"
                    color="var(--color-dark-800)"
                    left
                    >{{ mdiPencilOutline }}</VIcon
                  >
                  Complete Setup
                </VListItemTitle>
              </VListItem>
              <VListItem
                :disabled="selectedAccountId === account.id"
                @click="handleDeleteAccount(account.id)"
              >
                <VListItemTitle class="delete-label">
                  <VIcon
                    size="18"
                    color="var(--error-red)"
                    left
                    >{{ mdiTrashCanOutline }}</VIcon
                  >
                  Delete account
                </VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>

          <span class="account-info">
            <span class="bank-name">{{ account.businessName || account.label || 'Payout Account' }}</span>
            <span
              v-if="!account.onboardingComplete"
              class="pending-indicator"
              >Setup incomplete</span
            >
            <span
              v-else-if="selectedAccountId === account.id"
              class="selected-indicator"
              >Selected</span
            >
          </span>

          <span class="account-radio">
            <VIcon
              v-if="!account.onboardingComplete"
              size="22"
              color="#ccc"
              >{{ mdiLockOutline }}</VIcon
            >
            <VIcon
              v-else
              size="24"
              :color="selectedAccountId === account.id ? 'var(--color-dark-800)' : '#ccc'"
            >
              {{ selectedAccountId === account.id ? mdiRadioboxMarked : mdiRadioboxBlank }}
            </VIcon>
          </span>
        </button>
      </div>
    </div>

    <BKButton
      class="mb-6"
      white-text
      :loading="createMutation.isPending.value"
      @click="handleCreateAccount"
    >
      {{ accounts.length ? 'Connect another account' : 'Connect your bank account' }}
    </BKButton>

    <EventEditActionButtons
      :is-loading="false"
      :disabled="!selectedAccountId"
      :error="errorMessage"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />

    <StripeOnboardingModal
      v-model="showOnboarding"
      :client-secret="currentClientSecret || undefined"
      @complete="handleOnboardingComplete"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import {
  useCreatePayoutAccountMutation,
  useDeletePayoutAccountMutation,
  useEventPayoutAccountQuery,
  usePayoutAccountsQuery,
  useRefreshPayoutSessionMutation,
  useSetEventPayoutAccountMutation,
  useSyncPayoutAccountMutation,
} from '@/features/payoutAccounts/api/queries'
import StripeOnboardingModal from '@/features/payoutAccounts/ui/StripeOnboardingModal.vue'
import {
  mdiTrashCanOutline,
  mdiDotsHorizontal,
  mdiPencilOutline,
  mdiRadioboxMarked,
  mdiRadioboxBlank,
  mdiLockOutline,
} from '@mdi/js'

const deletePayoutAccountMutation = useDeletePayoutAccountMutation()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const errorMessage = ref<string>()
const { data: accountsData, isLoading: accountsLoading, refetch } = usePayoutAccountsQuery()
const { data: eventAccountData, isLoading: eventAccountLoading } = useEventPayoutAccountQuery(props.eventData.id)
const createMutation = useCreatePayoutAccountMutation()
const refreshMutation = useRefreshPayoutSessionMutation()
const setMutation = useSetEventPayoutAccountMutation(props.eventData.id)
const syncMutation = useSyncPayoutAccountMutation()

const showOnboarding = ref(false)
const currentClientSecret = ref<string | null>(null)
const currentAccountId = ref<number | null>(null)

const isLoading = computed(() => accountsLoading.value || eventAccountLoading.value)
const accounts = computed(
  () => accountsData.value || (eventAccountData.value?.account && [eventAccountData.value.account]) || []
)
const serverSelectedId = computed(() => eventAccountData.value?.account?.id || null)
const optimisticSelectedId = ref<number | null>(null)
const selectedAccountId = computed(() => optimisticSelectedId.value ?? serverSelectedId.value)

const handleCreateAccount = async () => {
  try {
    const result = await createMutation.mutateAsync({
      label: 'Payout Account',
    })
    currentAccountId.value = result.accountId
    currentClientSecret.value = result.clientSecret
    showOnboarding.value = true
  } catch (e) {
    console.error('Failed to create account:', e)
  }
}

const handleSelectAccount = async (accountId: number) => {
  if (!accountId) return
  optimisticSelectedId.value = accountId
  try {
    await setMutation.mutateAsync({ accountId })
  } catch (e) {
    optimisticSelectedId.value = null
    console.error('Failed to set payout account:', e)
  }
}

const handleContinueOnboarding = async (accountId: number) => {
  try {
    const result = await refreshMutation.mutateAsync(accountId)
    currentAccountId.value = accountId
    currentClientSecret.value = result.clientSecret
    showOnboarding.value = true
  } catch (e) {
    console.error('Failed to refresh session:', e)
  }
}

const handleOnboardingComplete = async () => {
  showOnboarding.value = false
  currentClientSecret.value = null

  // Sync account status from Stripe before refetching
  if (currentAccountId.value) {
    try {
      await syncMutation.mutateAsync(currentAccountId.value)
    } catch (e) {
      console.error('Failed to sync account status:', e)
    }
  }
  currentAccountId.value = null

  await refetch()

  // Auto-select the newly created account if it's the only one
  const refreshedAccounts = accountsData.value || []
  const completeAccounts = refreshedAccounts.filter((a) => a.onboardingComplete)
  if (completeAccounts.length === 1 && !selectedAccountId.value) {
    await handleSelectAccount(completeAccounts[0].id)
  }
}

const handleDeleteAccount = async (accountId: number) => {
  deletePayoutAccountMutation.mutate(accountId, {
    onSuccess: async () => {
      await refetch()
    },
    onError: (err) => {
      console.error('Failed to delete account:', err)
    },
  })
}

function handleNext() {
  emit('next')
}

function handleCancel() {
  emit('cancel')
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.payment-mode {
  max-width: 410px;
  margin: 140px auto 60px;

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 20px;
  }

  .form-title {
    margin-bottom: 0;
    color: var(--color-dark-800);
    font-size: 16px;
    line-height: 24px;
  }

  .form-subtitle {
    margin-bottom: 0;
    color: #999;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -1%;
  }

  .learn-more-btn {
    padding: 0;
    height: auto;
    min-height: 0;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-transform: none;
    align-self: flex-start;
  }

  .connected-accounts {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;

    .connected-accounts-item {
      display: flex;
      flex-direction: column;

      .connected-account {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px;
        border: 1px solid rgb(221 221 221 / 100%);
        border-radius: var(--border-radius-rounded);
        background-color: var(--color-white);
        cursor: pointer;
        width: 100%;
        text-align: left;

        &--selected {
          border-color: #8c7328;
        }

        &--pending {
          cursor: default;
          background-color: #fafafa;
          border-color: rgb(221 221 221 / 60%);
          opacity: 0.8;
        }

        .dots-menu {
          align-self: flex-start;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-dark-800);
          flex-shrink: 0;
          cursor: pointer;
        }

        .account-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          flex: 1;
          min-width: 0;
        }

        .bank-name {
          font-size: 16px;
          line-height: 24px;
          color: var(--color-dark-800);
        }

        .selected-indicator {
          color: #8c7328;
          font-size: 14px;
          line-height: 20px;
        }

        .pending-indicator {
          color: #999;
          font-size: 13px;
          line-height: 18px;
          font-weight: 400;
        }

        .account-radio {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
      }
    }
  }
}

.account-context-menu {
  min-width: 200px;
  border-radius: 12px;
  padding: 4px 0;

  .delete-label {
    color: var(--error-red);
  }
}

:deep(.bk-checkbox) {
  margin-top: 4px;
}
</style>
